import React, { ReactNode } from 'react';
import useObservable from '../../../../Shared/Hooks/useObservable.Hook';
import { getIsLoading } from '../../../Services/Loading.service';
import { getIsServerError } from '../../../Services/ServerError.service';
import ErrorState from '../../../Shared/EmptyStates/ErrorState/ErrorState';
import LoadingWrapper from '../../../Shared/LoadingWrapper/LoadingWrapper';
import ItemsWrapper from './ItemsWrapper/ItemsWrapper';
import TableHeaderWrapper from './TableHeaderWrapper/TableHeaderWrapper';
import { classes, st } from './TableWrapper.st.css';
import { getRootFolder } from '../../../Services/RootFolder.service';
import {
  Availability,
  Reason,
  Status,
} from '../../../../Constants/Permissions';
import { ServerErrorReason, ServerErrorState } from '../../../Observables/ServerError.observable';
import AccessModalsWrapper from '../../../Shared/EmptyStates/AccessModalsWrapper/AccessModalsWrapper';
import { getSelectedFolder } from '../../../Services/SelectedFolder.service';
import { getAppSettings } from '../../../Services/AppSettings.service';

interface TableContentProps {
  isLoading: boolean;
  children: ReactNode;
}

const TableContent = ({ isLoading, children }: TableContentProps) => (
  <div className={st(classes.tableContent, { isLoading })} role="rowgroup">
    {children}
  </div>
);

interface FolderItem {
  action: string;
  status: Status;
  reason?: Reason;
}

interface RootFolder {
  value: string;
  authorizeActions?: FolderItem[];
  noPlansAvailable?: boolean;
  pricingPlansIds?: string[];
}

interface SelectedFolder {
  libraryItems?: any[];
}

interface AppSettings {
  isMembersArea?: boolean;
}

const TableWrapper = () => {
  const [, isLoading] = useObservable(getIsLoading()) as [unknown, boolean];
  const [, isServerError] = useObservable(getIsServerError()) as [
    unknown,
    ServerErrorState,
  ];
  const [, rootFolder] = useObservable(getRootFolder()) as [
    unknown,
    RootFolder,
  ];
  const [, selectedFolder] = useObservable(getSelectedFolder()) as [
    unknown,
    SelectedFolder,
  ];
  const [, appSettings] = useObservable(getAppSettings()) as [
    unknown,
    AppSettings,
  ];

  const isMembersArea = appSettings?.isMembersArea ?? false;
  const shouldShowHeader =
    !isMembersArea ||
    (selectedFolder?.libraryItems && selectedFolder.libraryItems.length > 0);
  const viewPermissions = rootFolder?.authorizeActions?.find(
    (itemData: FolderItem) => itemData.action === 'VIEW',
  );
  const isForbidden = viewPermissions?.status === Status.FORBIDDEN;
  const availability =
    rootFolder?.noPlansAvailable === false ||
    rootFolder?.pricingPlansIds?.length
      ? Availability.PAID
      : Availability.MEMBER;

  if (isServerError?.isError) {
    if (isServerError.reason === ServerErrorReason.MEMBER_REQUIRED) {
      return (
        <AccessModalsWrapper
          folderName={rootFolder?.value}
          viewPermissions={{
            action: 'VIEW',
            status: Status.FORBIDDEN,
            reason: Reason.MUST_BE_A_MEMBER,
          }}
          availability={availability}
        />
      );
    }
    return <ErrorState />;
  }

  if (isLoading === undefined) {
    return null;
  }

  return (
    <>
      <div className={st(classes.tableContainer)} role="table">
        {shouldShowHeader && <TableHeaderWrapper />}
        {isLoading ? (
          <>
            <TableContent isLoading={true}>
              <ItemsWrapper />
            </TableContent>
            <div className={st(classes.tableLoading)}>
              <LoadingWrapper />
            </div>
          </>
        ) : (
          <TableContent isLoading={false}>
            {isForbidden ? (
              <AccessModalsWrapper
                folderName={rootFolder?.value}
                viewPermissions={viewPermissions}
                availability={availability}
              />
            ) : (
              <ItemsWrapper />
            )}
          </TableContent>
        )}
      </div>
    </>
  );
};

export default TableWrapper;
